import { useEffect, useState } from 'react'
import api from '../services/api'
import LookupSelect from './LookupSelect'

// Modal "Recapagem/Conserto do Pneu" — equivalente à tela legada TCUSPNU, aberta
// automaticamente depois de salvar um item da nota de compra com Cód. Pneu
// preenchido. Grava o histórico em frota.pnucus e, se Procedimento = Recapagem,
// avança a vida do pneu em frota.pnucad (fecha Km da vida atual, zera hodômetro,
// avança de vida e atualiza vida útil/almoxarifado).

interface Props {
  empcod: number
  serie: string
  numero: number
  pnvLin: number
  pnucod: number
  procedimento: 'S' | 'N' // vem do campo "Recapagem" do item (S/N) — só o default inicial, editável na tela
  dataEmissao: string
  valorInicial: number
  fornecedorNotaCod: string
  fornecedorNotaDes: string
  onClose: () => void
  onSalvo: () => void
}

const PROXIMA_VIDA: Record<string, string> = { '1V': '2V', '2V': '3V', '3V': '4V', '4V': '5V' }

// Domínio real do PnuCusRec (confirmado no combo box da tela legada TCUSPNA) —
// só 'S' avança a vida do pneu (PREPNEUa); as outras 4 só gravam histórico.
const PROCEDIMENTOS: { valor: 'S' | 'N' | 'G' | 'E' | 'D'; label: string }[] = [
  { valor: 'S', label: 'Recapagem' },
  { valor: 'N', label: 'Conserto' },
  { valor: 'G', label: 'Garantia' },
  { valor: 'E', label: 'Exame' },
  { valor: 'D', label: 'Danificado' },
]

function fmtMoeda(v: number) { return v.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }
function parseNumBr(v: string) {
  const s = String(v ?? '').trim()
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return Number(s) || 0
}
function fmtDataBr(iso: string) { return iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '-' }

// Linha "rótulo à esquerda / valor à direita", igual à tela legada — usada em vez
// da grade de 2 colunas padrão (`.form-grid-cad`) porque essa não deu espaço
// suficiente pros campos de lookup e estourava com scroll horizontal.
function Linha({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '150px 1fr', alignItems: 'center', gap: '0.8rem',
      padding: '0.4rem 0', borderBottom: '1px solid #f1f3f6',
    }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#57606f', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </div>
      {/* .field-cad (sem <label>) só pra herdar o estilo padrão de input/select do app */}
      <div className="field-cad" style={{ margin: 0 }}>{children}</div>
    </div>
  )
}

export default function ModalRecapagemPneu({
  empcod, serie, numero, pnvLin, pnucod, procedimento, dataEmissao, valorInicial,
  fornecedorNotaCod, fornecedorNotaDes, onClose, onSalvo,
}: Props) {
  const [carregando, setCarregando] = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState('')

  const [pneuDes, setPneuDes] = useState('')
  const [vidaAtual, setVidaAtual] = useState('1V')
  const [proc, setProc] = useState<'S' | 'N' | 'G' | 'E' | 'D'>(procedimento)

  const [valor] = useState(fmtMoeda(valorInicial))

  const [fornecedoraCod, setFornecedoraCod] = useState(fornecedorNotaCod)
  const [fornecedoraDes, setFornecedoraDes] = useState(fornecedorNotaDes)

  const [tipoDanoCod, setTipoDanoCod] = useState('')
  const [tipoDanoDes, setTipoDanoDes] = useState('')

  const [desenhoCod, setDesenhoCod] = useState('')
  const [desenhoDes, setDesenhoDes] = useState('')
  const [nsul, setNsul] = useState(5)
  const [sulcos, setSulcos] = useState(['0,0', '0,0', '0,0', '0,0', '0,0'])
  const [vidaUtil, setVidaUtil] = useState('')

  const [aplicacaoCod, setAplicacaoCod] = useState('')
  const [aplicacaoDes, setAplicacaoDes] = useState('')
  const [estocagemCod, setEstocagemCod] = useState('')
  const [estocagemDes, setEstocagemDes] = useState('')
  const [novoCodAlmox, setNovoCodAlmox] = useState('')
  const [novoCodAlmoxDes, setNovoCodAlmoxDes] = useState('')
  const [outraOperacao, setOutraOperacao] = useState('NAO')

  const isRecapagem = proc === 'S'
  const isDanificado = proc === 'D'
  const vidaProxima = PROXIMA_VIDA[vidaAtual] ?? null

  useEffect(() => {
    api.get(`/erp/pnucad/${pnucod}/atual`)
      .then(r => {
        setPneuDes(`${r.data.marca} · ${r.data.tipo}`.trim())
        setVidaAtual(r.data.vida || '1V')
        if (r.data.destino) { setAplicacaoCod(String(r.data.destino)) }
      })
      .catch(() => setErro('Não foi possível carregar os dados atuais do pneu.'))
      .finally(() => setCarregando(false))
  }, [pnucod])

  async function selecionarDesenho(cod: string, des: string) {
    setDesenhoCod(cod)
    setDesenhoDes(des)
    try {
      const r = await api.get(`/erp/pnunho/${cod}/detalhe`)
      const nsulNovo = Number(r.data.nhosul) || 0
      setNsul(nsulNovo)
      const altura = Number(r.data.nhoalt) || 0
      setSulcos(prev => prev.map((v, i) => i < nsulNovo ? altura.toFixed(1).replace('.', ',') : '—'))
      if (isRecapagem && vidaProxima) {
        const col = { '2V': 'nhovu2', '3V': 'nhovu3', '4V': 'nhovu4', '5V': 'nhovu5' }[vidaProxima] as string
        const vut = Number(r.data[col]) || 0
        if (vut) setVidaUtil(String(vut))
      }
    } catch { /* segue sem sugestão automática, usuário preenche na mão */ }
  }

  async function confirmar() {
    setErro('')
    const valorNum = parseNumBr(valor)
    if (valorNum < 0 || valorNum > 2000) return setErro('Valor deve estar entre R$ 0,00 e R$ 2.000,00.')
    if (!fornecedoraCod) return setErro('Informe a Fornecedora.')
    if (isDanificado && !tipoDanoCod) return setErro('Informe o Tipo de Dano.')
    if (isRecapagem && !desenhoCod) return setErro('Informe o Novo Desenho.')
    if (isRecapagem && !estocagemCod) return setErro('Informe o Ponto de Estocagem.')
    if (!aplicacaoCod) return setErro('Informe a Nova Aplicação.')
    if (isRecapagem && (!vidaUtil || Number(vidaUtil) < 10000 || Number(vidaUtil) > 150000)) {
      return setErro('Nova Vida Útil deve estar entre 10.000 e 150.000 Km.')
    }

    setSalvando(true)
    try {
      await api.post('/erp/pneu-recapagem', {
        empcod, serie, numero, pnvLin, pnucod,
        procedimento: proc,
        dataEmissao,
        valor: valorNum,
        fornecedoraCod,
        tipoDanoCod: tipoDanoCod ? Number(tipoDanoCod) : null,
        novoDesenhoCod: isRecapagem ? Number(desenhoCod) : null,
        sulcos: isRecapagem ? sulcos.map(s => parseNumBr(s === '—' ? '0' : s)) : null,
        novaAplicacaoCod: Number(aplicacaoCod),
        pontoEstocagemCod: isRecapagem ? estocagemCod : null,
        novoCodAlmoxItem: novoCodAlmox ? Number(novoCodAlmox) : null,
        outraOperacaoCod: outraOperacao,
      })
      onSalvo()
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar Recapagem/Conserto do pneu.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }}>
      <div className="modal-box modal-recapagem" style={{ width: 560, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h2>Recapagem / Conserto do Pneu</h2>
            <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>
              Pneu {pnucod}{pneuDes ? ` · ${pneuDes}` : ''}
            </div>
          </div>
          <button className="modal-fechar" onClick={onClose}>✕</button>
        </div>

        <div className="modal-corpo" style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.5rem' }}>
          {carregando ? (
            <p style={{ color: '#6b7280', textAlign: 'center', margin: '1rem 0' }}>Carregando dados do pneu...</p>
          ) : (
            <div>
              <Linha label="Data/Hora">
                <span style={{ fontSize: '0.85rem' }}>{fmtDataBr(dataEmissao)} 12:45</span>
              </Linha>

              <Linha label="Código Pneu">
                <span style={{ fontSize: '0.85rem' }}>{pnucod} · {pneuDes || '...'}</span>
              </Linha>

              <Linha label="Nº Lançamento">
                <span style={{ fontSize: '0.82rem', color: '#8a97a8', fontStyle: 'italic' }}>calculado automaticamente ao confirmar</span>
              </Linha>

              <Linha label="Procedimento">
                <select value={proc} onChange={e => setProc(e.target.value as typeof proc)} style={{ maxWidth: 220 }}>
                  {PROCEDIMENTOS.map(p => <option key={p.valor} value={p.valor}>{p.label}</option>)}
                </select>
              </Linha>

              {!isRecapagem && (
                <Linha label="Tipo de Dano">
                  <LookupSelect
                    endpoint="/erp/pnuret/lista" codKey="retcod" descKey="retdes" tagKey="retcod"
                    codValue={tipoDanoCod} descValue={tipoDanoDes}
                    onSelect={(cod, des) => { setTipoDanoCod(cod); setTipoDanoDes(des) }}
                    placeholder="Buscar dano cadastrado..." listWidth={320}
                  />
                </Linha>
              )}

              <Linha label="Valor">
                <input value={valor} readOnly disabled title="Vem automático do Val. Total do item da nota" style={{ maxWidth: 160 }} />
              </Linha>

              <Linha label="Fornecedora">
                <LookupSelect
                  endpoint="/erp/clientes/lista" codKey="cli_mat" descKey="cli_fan"
                  codValue={fornecedoraCod} descValue={fornecedoraDes}
                  onSelect={(cod, des) => { setFornecedoraCod(cod); setFornecedoraDes(des) }}
                  placeholder="Buscar fornecedora..." listWidth={320} somenteComTexto
                />
              </Linha>

              <Linha label="Nota Fiscal">
                <span style={{ fontSize: '0.85rem', display: 'flex', gap: '1rem' }}>
                  <span>Série <b>{serie}</b></span><span>Número <b>{numero}</b></span><span>Lin. <b>{pnvLin}</b></span>
                </span>
              </Linha>

              {isRecapagem && (
                <>
                  <Linha label="Novo Desenho">
                    <LookupSelect
                      endpoint="/erp/pnunho/lista" codKey="nhocod" descKey="nhodes" tagKey="nhocod"
                      codValue={desenhoCod} descValue={desenhoDes}
                      onSelect={selecionarDesenho}
                      placeholder="Buscar desenho do pneu..." listWidth={320}
                    />
                  </Linha>

                  <Linha label="Medida Sulcos">
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {sulcos.map((s, i) => (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ fontSize: '0.62rem', color: '#9aa2af', fontWeight: 700 }}>{i + 1}º</span>
                          <input
                            value={s} disabled={i >= nsul} style={{ width: 52, textAlign: 'center' }}
                            onChange={e => setSulcos(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                          />
                        </div>
                      ))}
                    </div>
                  </Linha>
                </>
              )}

              {isRecapagem && (
                <>
                  <Linha label="Nova Vida Pneu">
                    <input value={vidaProxima || '—'} readOnly disabled style={{ maxWidth: 120 }} />
                  </Linha>
                  <Linha label="Nova Vida Útil">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <input
                        value={vidaUtil} onChange={e => setVidaUtil(e.target.value.replace(/\D/g, ''))}
                        style={{ maxWidth: 140 }}
                      />
                      <span style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 600 }}>Km</span>
                    </div>
                  </Linha>
                </>
              )}

              <Linha label="Nova Aplicação">
                <LookupSelect
                  endpoint="/erp/pnudes/lista" codKey="pnudescod" descKey="pnudesdes" tagKey="pnudescod"
                  codValue={aplicacaoCod} descValue={aplicacaoDes}
                  onSelect={(cod, des) => { setAplicacaoCod(cod); setAplicacaoDes(des) }}
                  placeholder="Buscar aplicação..." listWidth={300}
                />
              </Linha>

              {isRecapagem && (
                <Linha label="Ponto de Estocagem">
                  <LookupSelect
                    endpoint="/erp/oedcad/lista" codKey="oed_cod" descKey="oed_des" tagKey="oed_cod"
                    extraParams={{ tipos: 5, empresa: empcod }}
                    codValue={estocagemCod} descValue={estocagemDes}
                    onSelect={(cod, des) => { setEstocagemCod(cod); setEstocagemDes(des) }}
                    placeholder="Buscar ponto de estocagem..." listWidth={300}
                  />
                </Linha>
              )}

              <Linha label="Novo Cód. Almox.">
                <LookupSelect
                  endpoint="/erp/itens-pneu/lista" codKey="its_cod" descKey="its_des" tagKey="its_cod"
                  codValue={novoCodAlmox} descValue={novoCodAlmoxDes}
                  onSelect={(cod, des) => { setNovoCodAlmox(cod); setNovoCodAlmoxDes(des) }}
                  placeholder="Buscar item (classe Pneu)..." listWidth={320}
                />
              </Linha>

              <Linha label="Outra Operação">
                <select value={outraOperacao} onChange={e => setOutraOperacao(e.target.value)} style={{ maxWidth: 140 }}>
                  <option value="NAO">Não</option>
                  <option value="SIM">Sim</option>
                </select>
              </Linha>
            </div>
          )}

          {erro && <div style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0.6rem 0' }}>{erro}</div>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmar} disabled={carregando || salvando}>
            {salvando ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
